import React, { useEffect, useState } from 'react';
import { Mail, X, Loader2, Check, AlertTriangle, Send, Eye } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';

/**
 * Phase 3A-5: Send Lesson Recap email modal.
 *
 *  - Tutor picks recipient(s): student / parent / both.
 *  - Parent email can be (a) already on the booking, or (b) entered ad-hoc
 *    with an optional "remember this parent email" toggle to persist it.
 *  - Branding rule: never expose Resend/Cloudinary/Daily.
 *  - Failsafe: PDF Download button is still rendered next to the modal so
 *    the tutor can grab the link manually if email delivery fails.
 */
const SendRecapModal = ({ open, onClose, recap, roomId, token, onSent }) => {
  const [recipients, setRecipients] = useState(null); // {student_email, parent_email, tutor_name, student_name}
  const [toStudent, setToStudent] = useState(true);
  const [toParent, setToParent] = useState(false);
  const [parentEmailOverride, setParentEmailOverride] = useState('');
  const [savePE, setSavePE] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    classroomApi.recapRecipients(roomId, token).then((r) => {
      setRecipients(r);
      // Default: send to student. If a parent email already exists, default-on parent too.
      setToStudent(true);
      setToParent(!!r?.parent_email);
      setParentEmailOverride('');
      setSavePE(false);
    }).catch(() => {});
  }, [open, roomId, token]);

  if (!open || !recap) return null;

  const effectiveParentEmail = (parentEmailOverride.trim() || recipients?.parent_email || '').toLowerCase();
  const canSubmit = (toStudent && recipients?.student_email) || (toParent && effectiveParentEmail);

  const submit = async () => {
    setSending(true);
    try {
      const r = await classroomApi.recapSend(roomId, token, recap.recap_id, {
        to_student: toStudent,
        to_parent: toParent,
        parent_email_override: toParent && parentEmailOverride.trim() ? parentEmailOverride.trim() : null,
        save_parent_email: toParent && savePE,
        include_short_summary: true,
      });
      if (r.overall_status === 'success') {
        toast.success('Recap sent ✓');
      } else if (r.overall_status === 'partial') {
        toast.error('Recap partially sent — please check delivery details');
      } else {
        toast.error('Recap could not be sent');
      }
      onSent?.(r);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Send failed');
    } finally { setSending(false); }
  };

  const lastDelivery = recap.delivery;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
         onClick={() => !sending && onClose()} data-testid="recap-send-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-red-700" />
            <div className="text-sm font-bold uppercase tracking-wider">Send Lesson Recap</div>
          </div>
          <button onClick={() => !sending && onClose()} disabled={sending}
                  className="h-7 w-7 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                  data-testid="recap-send-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="text-[11px] text-gray-500">
            Sending <span className="font-bold text-gray-900">{recap.filename}</span> — {recap.lesson_track || recap.subject} · {recipients?.tutor_name} → {recipients?.student_name}
          </div>

          {/* Recipient checkboxes */}
          <div className="space-y-2">
            <label className={`flex items-start gap-3 p-3 rounded-lg border ${toStudent ? 'border-red-200 bg-red-50/50' : 'border-gray-200'} ${recipients?.student_email ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
              <input type="checkbox" checked={toStudent} disabled={!recipients?.student_email}
                onChange={(e) => setToStudent(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-red-700" data-testid="send-to-student" />
              <div className="flex-1">
                <div className="text-xs font-bold text-gray-900">Student</div>
                <div className="text-[11px] text-gray-500 font-mono">{recipients?.student_email || 'No student email on this booking'}</div>
              </div>
              {lastDelivery?.sent_to_student && (
                <span className="text-[10px] bg-teal-700 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider flex items-center gap-0.5">
                  <Check className="h-2.5 w-2.5" /> sent
                </span>
              )}
            </label>

            <label className={`flex items-start gap-3 p-3 rounded-lg border ${toParent ? 'border-red-200 bg-red-50/50' : 'border-gray-200'} cursor-pointer`}>
              <input type="checkbox" checked={toParent} onChange={(e) => setToParent(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-red-700" data-testid="send-to-parent" />
              <div className="flex-1">
                <div className="text-xs font-bold text-gray-900">Parent / Guardian</div>
                {recipients?.parent_email && !parentEmailOverride && (
                  <div className="text-[11px] text-gray-500 font-mono">{recipients.parent_email}</div>
                )}
                {(toParent && (!recipients?.parent_email || parentEmailOverride !== '')) && (
                  <div className="mt-2 space-y-1.5">
                    <input type="email" value={parentEmailOverride} onChange={(e) => setParentEmailOverride(e.target.value)}
                      placeholder={recipients?.parent_email ? 'Override parent email' : 'parent@example.com'}
                      className="w-full h-8 px-2 rounded-md border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-red-200"
                      data-testid="parent-email-input" onClick={(e) => e.stopPropagation()} />
                    <label className="flex items-center gap-2 text-[10px] text-gray-600 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={savePE} onChange={(e) => setSavePE(e.target.checked)}
                        className="h-3 w-3 accent-red-700" data-testid="parent-email-save" />
                      Remember this email for future recaps
                    </label>
                  </div>
                )}
              </div>
              {lastDelivery?.sent_to_parent && (
                <span className="text-[10px] bg-teal-700 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider flex items-center gap-0.5">
                  <Check className="h-2.5 w-2.5" /> sent
                </span>
              )}
            </label>
          </div>

          {/* Preview link */}
          {recap.secure_url && (
            <a href={recap.secure_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-[11px] text-gray-600 hover:text-gray-900 underline"
              data-testid="recap-send-preview">
              <Eye className="h-3 w-3" /> Preview the PDF before sending
            </a>
          )}

          {/* Last delivery summary */}
          {lastDelivery?.last_sent_at && (
            <div className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-2.5" data-testid="recap-send-history">
              Last sent: {new Date(lastDelivery.last_sent_at).toLocaleString()} · status: <span className={`font-bold ${lastDelivery.last_status === 'success' ? 'text-teal-700' : lastDelivery.last_status === 'partial' ? 'text-amber-700' : 'text-red-700'}`}>{lastDelivery.last_status}</span>
            </div>
          )}

          {/* Failure notice if any history entry shows failure */}
          {lastDelivery?.last_status && lastDelivery.last_status !== 'success' && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-amber-900 leading-snug">
                The last attempt was not fully successful. The PDF download still works — you can resend from this dialog.
              </div>
            </div>
          )}

          <div className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3">
            We'll send a branded Winhouse email with a one-click link to the recap PDF. No platform vendor details are revealed.
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <button onClick={() => !sending && onClose()} disabled={sending}
            className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider hover:bg-gray-100"
            data-testid="recap-send-cancel">Cancel</button>
          <button onClick={submit} disabled={sending || !canSubmit}
            className="h-9 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5"
            data-testid="recap-send-submit">
            {sending ? <><Loader2 className="h-3 w-3 animate-spin" /> Sending…</> : <><Send className="h-3 w-3" /> Send Recap</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendRecapModal;
